import { ref, onUnmounted } from 'vue';

/**
 * Dictation through the browser's own speech recognition.
 *
 * No backend and no cost per minute, at the price of only existing in Chromium browsers:
 * `isSupported` is false everywhere else, and callers hide the button rather than offer
 * something that will not work.
 *
 * Words are written into `target` as they are spoken, provisional guesses included. Waiting
 * for the recogniser to settle on a phrase leaves the field empty until the speaker pauses,
 * which reads as the microphone not working.
 *
 * @param {Object} options
 * @param {import('vue').Ref<string>} options.target The text the dictation appends to.
 * @param {string} options.lang BCP-47 tag, e.g. 'es-ES'.
 */
export function useDictation({ target, lang = 'es-ES' } = {}) {
  const SpeechRecognition =
    window.SpeechRecognition ?? window.webkitSpeechRecognition;
  const isSupported = Boolean(SpeechRecognition);
  const isListening = ref(false);

  let recognition = null;
  // What was already in the field when dictation started; spoken text goes after it.
  let base = '';
  // The last value this composable wrote, to tell its own writes from the user's typing.
  let lastWritten = '';

  const stop = () => {
    recognition?.stop();
    recognition = null;
    isListening.value = false;
  };

  const write = spoken => {
    const text = spoken.trim();
    let value = base;
    if (text) value = base ? `${base} ${text}` : text;

    lastWritten = value;
    target.value = value;
  };

  const start = () => {
    if (!isSupported || isListening.value) return;

    base = (target.value ?? '').trim();
    lastWritten = target.value ?? '';

    recognition = new SpeechRecognition();
    recognition.lang = lang;
    // Keep going through natural pauses; the user stops it when they are done.
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = event => {
      // The field changed under us, so the user typed while dictating. Their edit wins:
      // rebuilding from `base` would silently throw it away.
      if (target.value !== lastWritten) {
        stop();
        return;
      }

      // Rebuilt from every result of the session on each event, settled and provisional
      // alike. A provisional guess is replaced in place when it settles, instead of being
      // appended a second time.
      const spoken = Array.from(event.results)
        .map(result => result[0].transcript.trim())
        .join(' ');
      write(spoken);
    };

    recognition.onerror = () => {
      isListening.value = false;
    };

    // The browser ends the session by itself after a long silence. Whatever was on screen
    // stays: making words the user already saw disappear would look like a bug too.
    recognition.onend = () => {
      isListening.value = false;
    };

    recognition.start();
    isListening.value = true;
  };

  const toggle = () => (isListening.value ? stop() : start());

  onUnmounted(stop);

  return { isSupported, isListening, start, stop, toggle };
}
